import { Controller } from "@hotwired/stimulus";

// Drives step1's results list: polls GET /search/status until the backend
// search is ready, then fetches the same server-rendered fragment the
// mobile flow already uses (Step1Controller#step_1_reload / #step_1_more,
// via <path>/reload and <path>/more/:offset -- unmodified, UA-agnostic
// actions; render_view resolves to the new front/step_1_air_search_results
// partial for a desktop request instead of front_mobile's, see
// lib/render_helper.rb#find_layout_view) and swaps/appends the resulting
// HTML. Replaces the old Vue+Vuex+SearchChannel combo, which itself was
// never a real push/WebSocket channel -- confirmed by reading channel.ts:
// the WebSocket path is entirely commented out, it's a client-driven HTTP
// poll either way, just RPC'd through F-ENGINE's /capi endpoint directly
// instead of through this app's own controller actions.
export default class extends Controller {
  static targets = ["results", "list", "loadMore", "loadMoreButton", "loadMoreSpinner", "filterForm", "loading", "error", "rulesPanel", "overlayTemplate"];
  static values = { searchId: String, orderBy: String };

  connect() {
    this.basePath = window.location.pathname;
    this.poll();
  }

  disconnect() {
    clearTimeout(this.pollTimer);
    this.stopAutoLoad();
  }

  // --- infinite scroll ---------------------------------------------------
  //
  // Watches the load-more sentinel and fetches the next page as it comes
  // into view. rootMargin starts the fetch ~600px before the sentinel is
  // actually on screen, so the next cards are usually already there by the
  // time the customer reaches the bottom.
  //
  // Re-attached after every reload() (which replaces resultsTarget's whole
  // innerHTML) and after every loadMore() (which replaces the sentinel), so
  // the observer always points at the element currently in the DOM.
  observeLoadMore() {
    this.stopAutoLoad();
    if (!this.hasLoadMoreTarget) return;

    // No IntersectionObserver: leave the button alone and let it do the job.
    if (typeof IntersectionObserver === "undefined") return;

    if (this.hasLoadMoreButtonTarget) this.loadMoreButtonTarget.classList.add("hidden");
    if (this.hasLoadMoreSpinnerTarget) {
      this.loadMoreSpinnerTarget.classList.remove("hidden");
      this.loadMoreSpinnerTarget.classList.add("flex");
    }

    this.autoLoadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      },
      { rootMargin: "600px 0px" }
    );
    this.autoLoadObserver.observe(this.loadMoreTarget);
  }

  stopAutoLoad() {
    if (!this.autoLoadObserver) return;
    this.autoLoadObserver.disconnect();
    this.autoLoadObserver = null;
  }

  // Back to a button the customer can press -- used when a page fetch fails,
  // so a dropped request doesn't silently end the results.
  showLoadMoreButton() {
    this.stopAutoLoad();
    if (this.hasLoadMoreButtonTarget) this.loadMoreButtonTarget.classList.remove("hidden");
    if (this.hasLoadMoreSpinnerTarget) {
      this.loadMoreSpinnerTarget.classList.add("hidden");
      this.loadMoreSpinnerTarget.classList.remove("flex");
    }
  }

  // How many ITINERARIES are on screen. Not listTarget.children.length: each
  // result renders a card AND its own hidden "Visa resa" overlay as siblings
  // (plus a banner after every fourth), so children.length was double the
  // real count -- measured 30 for 15 results on a live fragment. Every
  // "load more" therefore asked for offset 30 and skipped 15 results
  // outright. Counting cards is the number the server's offset means.
  loadedResultCount() {
    if (!this.hasListTarget) return 0;
    return this.listTarget.querySelectorAll('[data-step1-results-target="card"]').length;
  }

  poll() {
    fetch(`/search/status?search_id=${encodeURIComponent(this.searchIdValue)}`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "ready") {
          this.reload();
        } else if (data.status === "error") {
          this.showError();
        } else {
          this.pollTimer = setTimeout(() => this.poll(), 800);
        }
      })
      .catch(() => {
        this.pollTimer = setTimeout(() => this.poll(), 1500);
      });
  }

  // The filter form's own change handler (see _step_1_filter.html.erb) --
  // debounced 500ms, matching mobile's own #filter_form behaviour exactly
  // (front_mobile/step_1.js.coffee's `filter_timer`), so ticking several
  // airline checkboxes in a row fires one request for the settled
  // selection instead of one per checkbox. Sort tabs (setOrderBy) call
  // reload() directly, bypassing this -- a single deliberate click doesn't
  // need debouncing.
  debouncedReload(event) {
    if (event) event.preventDefault();
    // Flags the reload this schedules as a real user filter change, which
    // is what tells Step1Controller to persist the selection. The automatic
    // first load submits this same form at its defaults, so without the
    // distinction every page view would overwrite the saved selection with
    // "no filters" before restoreSavedFilters() could put it back.
    this.userFilterAction = true;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.reload(), 500);
  }

  // Also the filter form's debounced change handler above and the sort
  // tabs (setOrderBy) -- anything that changes which results should show
  // always re-hits the server rather than filtering client-side, matching
  // how filtering already worked before this conversion (a real F-ENGINE
  // re-filter call, not a client-side array filter).
  reload(event) {
    if (event) event.preventDefault();

    const params = this.hasFilterFormTarget ? new URLSearchParams(new FormData(this.filterFormTarget)) : new URLSearchParams();
    // Sort tabs are re-rendered wholesale inside the results fragment on
    // every reload (their active state reads params[:filter_order_by]
    // server-side), unlike the sidebar filter form -- tracked as a
    // controller value instead of a DOM element so a filter checkbox
    // change doesn't reset back to the default sort.
    if (this.orderByValue) params.set("filter_order_by", this.orderByValue);
    if (this.userFilterAction) {
      params.set("filter_action", "1");
      this.userFilterAction = false;
    }

    fetch(`${this.basePath}/reload?${params.toString()}`, { headers: { Accept: "text/html" } })
      .then((response) => response.text())
      .then((html) => {
        if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden");
        this.resultsTarget.innerHTML = html;
        this.onFirstLoadComplete();
        // innerHTML replaced the sentinel along with everything else.
        this.observeLoadMore();
      })
      .catch(() => this.showError());
  }

  // Runs once, after the FIRST reload response ever lands (whether that's
  // the automatic poll-triggered one, or -- in principle -- a filter/sort
  // action that happened to win the race, see enableFilters below).
  //
  // Two things need this:
  //  - The filter sidebar's travel-time slider ships with generous static
  //    bounds (see _step_1_filter.html.erb) because @search.filter_max
  //    isn't known until after the first real fetch. Once it is, tighten
  //    the slider to the real min/max for this search.
  //  - filter_params (Step1Controller) needs airline/airport codes cached
  //    (Rails.cache, written server-side right after eager_load_result_
  //    values on *this same* request) before it can safely build the
  //    "everything selected" alliance/airline/connection_point fallback
  //    any real filter/sort action requires (see its own long comment).
  //    Before that cache exists, the fallback is an *empty* hash, which
  //    F-ENGINE reads as "select nothing" and deletes every result --
  //    confirmed live: touching a filter/slider before this first load
  //    finished reliably zeroed out results. The filter sidebar ships
  //    `disabled` (see _step_1_filter.html.erb) specifically so this race
  //    can't happen -- enabling it only here, after this same request has
  //    guaranteed the cache exists for the next one.
  //  - The alliance/airline/connection_point checkboxes themselves don't
  //    exist in the static sidebar at all yet -- clone them in from the
  //    <template> this same results fragment carries (see its own long
  //    comment), replacing the sidebar's placeholder.
  onFirstLoadComplete() {
    if (this.firstLoadComplete) return;
    this.firstLoadComplete = true;

    if (this.hasFilterFormTarget) {
      this.filterFormTarget.querySelectorAll("input:disabled").forEach((el) => (el.disabled = false));
      const note = this.filterFormTarget.querySelector('[data-role="filterLoadingNote"]');
      if (note) note.classList.add("hidden");

      const template = this.resultsTarget.querySelector('template[data-role="filterExtrasTemplate"]');
      const placeholder = this.filterFormTarget.querySelector('[data-role="filterExtrasPlaceholder"]');
      if (template && placeholder) placeholder.replaceWith(template.content.cloneNode(true));
    }

    const bounds = this.resultsTarget.querySelector("[data-min-traveltime]");
    if (bounds && this.hasFilterFormTarget) {
      const filterController = this.application.getControllerForElementAndIdentifier(this.filterFormTarget, "step1-filter");
      if (filterController) filterController.applyTravelTimeBounds(bounds.dataset.minTraveltime, bounds.dataset.maxTraveltime);
    }

    this.restoreSavedFilters();
  }

  // Re-applies the filter selection the customer last made on this site
  // (Step1Controller#step1_saved_filters -> gon.saved_filters; the server
  // owns the 24h expiry and drops the set entirely when someone arrives
  // from a meta site or any other external link).
  //
  // Deliberately runs from onFirstLoadComplete rather than connect(): the
  // alliance/airline/connection_point checkboxes don't exist until the
  // extras template has been cloned in just above, and the inputs are
  // disabled until then anyway -- filtering before that first load
  // completes is the race the disabled state exists to prevent (see
  // _step_1_filter.html.erb).
  restoreSavedFilters() {
    const saved = (typeof window !== "undefined" && window.gon && window.gon.saved_filters) || null;
    if (!saved || !this.hasFilterFormTarget) return;

    // Flatten { alliance: { SK: "1" } } back into the "alliance[SK]" form
    // field names the sidebar actually uses.
    const wanted = new Map();
    Object.entries(saved).forEach(([key, value]) => {
      if (value && typeof value === "object") {
        Object.entries(value).forEach(([sub, inner]) => wanted.set(`${key}[${sub}]`, String(inner)));
      } else if (value !== null && value !== undefined) {
        wanted.set(key, String(value));
      }
    });
    if (wanted.size === 0) return;

    // Only fields this feature actually owns, so an unrelated input inside
    // the form could never be silently unchecked by the rule below.
    const managed = /^(filter_|alliance\[|airline\[|connection_point\[)/;
    let changed = false;

    this.filterFormTarget.querySelectorAll("input[name]").forEach((input) => {
      if (!managed.test(input.name)) return;

      if (input.type === "checkbox") {
        // An unchecked box is simply absent from a submitted form, so
        // "not in the saved set" is what unchecked means here -- this has
        // to drive both directions, since alliance/airline/connection_point
        // all render checked by default.
        const next = wanted.has(input.name);
        if (input.checked !== next) {
          input.checked = next;
          changed = true;
        }
      } else if (wanted.has(input.name)) {
        const next = wanted.get(input.name);
        if (input.value !== next) {
          input.value = next;
          changed = true;
          // "input" (not "change") on purpose: it drives step1-filter's own
          // label updates without bubbling into the form's
          // change->debouncedReload binding, so restoring stays one reload.
          input.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    });

    // Not flagged as a user filter action: restoring must not re-stamp the
    // saved-at timestamp, or a set would never reach its 24h expiry as long
    // as someone kept browsing.
    if (changed) this.reload();
  }

  setOrderBy(event) {
    this.orderByValue = event.currentTarget.dataset.orderBy;
    this.reload();
  }

  loadMore(event) {
    if (event) event.preventDefault();

    // The observer fires repeatedly while the sentinel stays in view, so
    // without this guard scrolling to the bottom would kick off a fetch per
    // frame, all with the same offset.
    if (this.loadingMore) return;
    this.loadingMore = true;

    const offset = this.loadedResultCount();

    fetch(`${this.basePath}/more/${offset}`, { headers: { Accept: "text/html" } })
      .then((response) => (response.ok ? response.text() : ""))
      .then((html) => {
        if (!html) {
          // Server had nothing left -- take the sentinel away and stop
          // watching, otherwise the observer keeps firing at the bottom.
          this.stopAutoLoad();
          if (this.hasLoadMoreTarget) this.loadMoreTarget.remove();
          return;
        }

        const fragment = document.createElement("div");
        fragment.innerHTML = html;

        const newList = fragment.querySelector('[data-step1-results-target="list"]');
        if (newList && this.hasListTarget) {
          Array.from(newList.children).forEach((card) => this.listTarget.appendChild(card));
        }

        const newLoadMore = fragment.querySelector('[data-step1-results-target="loadMore"]');
        if (this.hasLoadMoreTarget) {
          if (newLoadMore) {
            this.loadMoreTarget.outerHTML = newLoadMore.outerHTML;
          } else {
            this.stopAutoLoad();
            this.loadMoreTarget.remove();
          }
        }

        // Point the observer at the sentinel that just replaced the old one.
        this.observeLoadMore();
      })
      .catch(() => this.showLoadMoreButton())
      .finally(() => {
        this.loadingMore = false;
      });
  }

  showDetail(event) {
    const uuid = event.currentTarget.dataset.uuid;
    const overlay = this.detailOverlayFor(uuid);
    if (!overlay) return;
    overlay.classList.remove("hidden");
    overlay.classList.add("flex");
    document.body.classList.add("modal-open");
  }

  // The overlays ship as <template> content (see _step_1_result.html.erb),
  // so the first open is what puts one in the DOM. Cloned in next to its own
  // template to keep the card/overlay sibling order the rest of this file
  // assumes, and left there afterwards: reopening the same itinerary should
  // still show whatever baggage the visitor picked last time.
  detailOverlayFor(uuid) {
    const selector = `[data-step1-results-target="overlay"][data-overlay-uuid="${uuid}"]`;
    const existing = this.element.querySelector(selector);
    if (existing) return existing;

    const template = this.element.querySelector(
      `template[data-step1-results-target="overlayTemplate"][data-overlay-uuid="${uuid}"]`
    );
    if (!template) return null;

    template.parentNode.insertBefore(template.content.cloneNode(true), template.nextSibling);
    return this.element.querySelector(selector);
  }

  // Backdrop click. This lives here rather than on itinerary-detail (whose
  // own #close does the same thing) purely because of Stimulus scope: that
  // controller is declared on the overlay's <form>, and the backdrop is a
  // SIBLING of that form, not a descendant -- so a
  // click->itinerary-detail#close on it silently never binds, which is why
  // only the header's back arrow ever closed the modal. The form has to keep
  // the controller (it does form.action / new FormData(form) on submit), and
  // this controller already wraps the whole results list, backdrops included.
  closeDetail(event) {
    const overlay = event.currentTarget.closest('[data-step1-results-target="overlay"]');
    if (!overlay) return;
    overlay.classList.add("hidden");
    document.body.classList.remove("modal-open");
  }

  toggleRules(event) {
    event.preventDefault();
    event.stopPropagation();
    const card = event.currentTarget.closest('[data-step1-results-target="card"]');
    const panel = card && card.querySelector('[data-step1-results-target="rulesPanel"]');
    if (panel) panel.classList.toggle("hidden");
  }

  showError() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden");
    if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden");
  }
}
